import React, {useState} from 'react';

import {TeamBadgeList} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/TeamBadgeList';
import {
  excludeTeams,
  getCommonDocument,
} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/utils';
import {useTeams} from 'app/teams/api';
import {
  getPoliciesUrlForTeam,
  getProceduresUrlForTeam,
  getProcessesUrlForTeam,
  getSignoffUrlForTeam,
} from 'app/urls';
import {UNLIMITED_PAGE_SIZE} from 'app/utils/constants';
import {gt, ngt} from 'app/utils/i18n';

import type {BaseActionableDocumentType} from 'app/general/types';
import type {BasicTeamSerializedType} from 'sweet-types';

type Props = {
  document: BaseActionableDocumentType;
  signoffEnabledTeamUrls: Set<string>;
  contentType: 'procedure' | 'policy' | 'proceduregroup';
};

export function SignoffTeamsList({
  document,
  signoffEnabledTeamUrls,
  contentType,
}: Props) {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  const {data: allTeamsResponse, isLoading} = useTeams({
    page: '1',
    page_size: `${UNLIMITED_PAGE_SIZE}`,
  });

  if (isLoading) {
    return null;
  }

  const allTeams =
    allTeamsResponse?.results.filter((team) => team.can_edit) ?? [];

  const documentTeamUrls = new Set(
    getCommonDocument(document).team_memberships.map((m) => m.team)
  );
  const documentTeams = allTeams.filter((team) =>
    documentTeamUrls.has(team.url)
  );

  const teamsWithSignoffsAll = documentTeams.filter((team) =>
    signoffEnabledTeamUrls.has(team.url)
  );

  const disabledSignoffsTeams = documentTeams.filter(
    (team) => !signoffEnabledTeamUrls.has(team.url)
  );
  const docNotAttachedTeams = excludeTeams(allTeams, documentTeams);

  return (
    <div>
      <TeamBadgeList
        text={ngt(
          'Members of this team will be required to sign off this document.',
          'Members of these teams will be required to sign off this document.',
          teamsWithSignoffsAll.length
        )}
        allTeams={teamsWithSignoffsAll}
        collapse={true}
      />

      {docNotAttachedTeams.length > 0 || disabledSignoffsTeams.length > 0 ? (
        <button
          type="button"
          className="btn btn-link"
          onClick={() => {
            setIsCollapsed(!isCollapsed);
          }}
        >
          <small>
            {isCollapsed ? gt('Show other teams') : gt('Collapse')}{' '}
            <i className={`fa fa-chevron-${isCollapsed ? 'down' : 'up'}`} />
          </small>
        </button>
      ) : null}

      {isCollapsed ? null : (
        <>
          <TeamBadgeList
            text={gt(
              'The "When managers choose" Sign Off rule is not enabled for these teams'
            )}
            allTeams={disabledSignoffsTeams}
            urlExtractor={(team: BasicTeamSerializedType) =>
              getSignoffUrlForTeam(team)
            }
          />
          <TeamBadgeList
            text={gt('This document is not attached to these teams.')}
            urlExtractor={(team: BasicTeamSerializedType) =>
              contentType === 'procedure'
                ? getProceduresUrlForTeam(team)
                : contentType === 'proceduregroup'
                ? getProcessesUrlForTeam(team)
                : contentType === 'policy'
                ? getPoliciesUrlForTeam(team)
                : team.html_url
            }
            allTeams={docNotAttachedTeams}
          />
        </>
      )}
    </div>
  );
}
